import { Span, isObject, isString } from "@hopper-ui/components";
import { type InitializingCategoryKey, isTenantScanning } from "../../api/crawlCategories.ts";
import { client } from "../../api/client.ts";
import { initialCrawlOverviewQueryOptions } from "../../api/routes/initial-crawl-overview.ts";
import { siteCollectionDetailsQueryOptions } from "../../api/routes/odata/details.ts";
import { MIN_USER_SEARCH_TERM_LENGTH, searchSiteCollectionsQueryOptions } from "../../api/routes/odata/search.ts";
import { WorkspaceIcon, type WorkspaceType, getMainWorkspaceType } from "@sg-protect/components";
import { isEdmGuid } from "@sg-protect/core";
import {
    type ProtectRecentlyViewedEntry,
    type ProtectSearchProviderOptions,
    type ProtectSearchResult,
    useInstallWorkspaceHeader
} from "@sg-protect/shell";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { generatePath, useMatch } from "react-router";
import { getPaths } from "../../paths.ts";
import { useSearchPaths } from "./searchPaths.ts";

const PROVIDER_ID = "workspaces";

const WORKSPACE_TRIGGER = "Workspace";

const RECENT_WORKSPACE_TRIGGER = "Recently Viewed Workspace";

const SCANNING_WARNING = "Initial scan in progress. Some results may be missing until it completes.";

const SEARCHABLE_CATEGORY_KEYS: readonly InitializingCategoryKey[] = [
    "isPeopleAndGroupsInitializing",
    "isSitesAndActivityInitializing"
];

const RECORDABLE_WORKSPACE_TYPES: readonly WorkspaceType[] = ["group", "oneDrive", "sharePointSite", "teams"];

const WORKSPACE_TYPES = new Set<string>(
    Object.keys({
        group: 0,
        team: 0,
        teams: 0,
        oneDrive: 0,
        sharePointSite: 0,
        teamsChannel: 0,
        unknown: 0
    } satisfies Record<WorkspaceType, number>)
);

interface RecentWorkspaceData {
    workspaceType: WorkspaceType;
}

function isRecentWorkspaceData(value: unknown): value is RecentWorkspaceData {
    return isObject(value) && isString(value.workspaceType) && WORKSPACE_TYPES.has(value.workspaceType);
}

function useResults(query: string, isEnabled: boolean) {
    const paths = useSearchPaths();
    const isWorkspaceScoped = useInstallWorkspaceHeader(client);

    const {
        data: siteCollections,
        isFetching,
        isError
    } = useQuery({
        ...searchSiteCollectionsQueryOptions(query),
        enabled: isEnabled
    });

    const { data: crawlOverview } = useQuery({
        ...initialCrawlOverviewQueryOptions(),
        enabled: isWorkspaceScoped
    });

    const results = useMemo(() => {
        if (siteCollections === undefined || paths === undefined) {
            return undefined;
        }

        return siteCollections.flatMap<ProtectSearchResult>(siteCollection => {
            if (!siteCollection.displayName) {
                return [];
            }

            const workspaceType = getMainWorkspaceType(siteCollection.types);

            return [
                {
                    id: siteCollection.id,
                    displayName: siteCollection.displayName,
                    to: generatePath(paths.workspaceDetails, { siteCollectionId: siteCollection.id }),
                    trackingTrigger: WORKSPACE_TRIGGER,
                    icon: <WorkspaceIcon type={workspaceType} isDecorative display="flex" />
                }
            ];
        });
    }, [siteCollections, paths]);

    return {
        results,
        isFetching,
        isError,
        warning: isTenantScanning(crawlOverview?.primary, SEARCHABLE_CATEGORY_KEYS) ? (
            <Span data-public>{SCANNING_WARNING}</Span>
        ) : undefined
    };
}

function useRecentResults(entries: ProtectRecentlyViewedEntry[]) {
    const paths = useSearchPaths();

    return useMemo(() => {
        if (paths === undefined) {
            return [];
        }

        return entries.flatMap<ProtectSearchResult>(entry =>
            isRecentWorkspaceData(entry.data)
                ? [
                      {
                          id: entry.id,
                          displayName: entry.displayName,
                          to: generatePath(paths.workspaceDetails, { siteCollectionId: entry.id }),
                          trackingTrigger: RECENT_WORKSPACE_TRIGGER,
                          icon: <WorkspaceIcon type={entry.data.workspaceType} isDecorative display="flex" />
                      }
                  ]
                : []
        );
    }, [entries, paths]);
}

function useVisitedEntry() {
    const match = useMatch({ path: getPaths().workspaceDetails, end: false });
    const siteCollectionId = match?.params.siteCollectionId;

    const { data: siteCollection } = useQuery({
        ...siteCollectionDetailsQueryOptions(siteCollectionId ?? ""),
        enabled: isEdmGuid(siteCollectionId)
    });

    return useMemo(() => {
        if (!siteCollection?.id || !siteCollection.displayName) {
            return undefined;
        }

        const workspaceType = getMainWorkspaceType(siteCollection.types ?? []);

        if (!RECORDABLE_WORKSPACE_TYPES.includes(workspaceType)) {
            return undefined;
        }

        return {
            providerId: PROVIDER_ID,
            id: siteCollection.id,
            displayName: siteCollection.displayName,
            data: { workspaceType } satisfies RecentWorkspaceData
        };
    }, [siteCollection]);
}

export const workspacesSearchProvider: ProtectSearchProviderOptions = {
    id: PROVIDER_ID,
    priority: 20,
    minQueryLength: MIN_USER_SEARCH_TERM_LENGTH,
    entityLabels: ["workspaces", "OneDrives"],
    sectionLabel: <Span data-public>Workspaces and OneDrives</Span>,
    recentSectionLabel: <Span data-public>Recently viewed workspaces and OneDrives</Span>,
    useResults,
    useRecentResults,
    useVisitedEntry
};
